import { create } from "zustand";

// Storage keys
const RENDER_TALLY_KEY = "henophess_render_tally";
const OPEN_ACCORDION_INDEXES_KEY = "henophess_open_accordion_indexes";

// Helper function to load render tallies from localStorage
const loadRenderTally = (): Map<string, number> => {
  try {
    if (typeof window === "undefined") return new Map<string, number>();

    const storedTally = localStorage.getItem(RENDER_TALLY_KEY);
    if (storedTally) {
      // Convert the stored object back to a Map
      const tallyObject = JSON.parse(storedTally);
      return new Map<string, number>(Object.entries(tallyObject));
    }
  } catch (error) {
    console.error("Failed to load render tally from localStorage:", error);
  }

  return new Map<string, number>();
};

// Helper function to save render tallies to localStorage
const saveRenderTally = (tally: Map<string, number>): void => {
  try {
    if (typeof window === "undefined") return;

    // Convert Map to object for storage
    const tallyObject = Object.fromEntries(tally.entries());
    localStorage.setItem(RENDER_TALLY_KEY, JSON.stringify(tallyObject));
  } catch (error) {
    console.error("Failed to save render tally to localStorage:", error);
  }
};

// Helper function to load open accordion indexes from localStorage
const loadOpenAccordionIndexes = (): number[] => {
  try {
    if (typeof window === "undefined") return [];

    const storedIndexes = localStorage.getItem(OPEN_ACCORDION_INDEXES_KEY);
    if (storedIndexes) {
      return JSON.parse(storedIndexes);
    }
  } catch (error) {
    console.error("Failed to load open accordion indexes from localStorage:", error);
  }

  return [];
};

// Helper function to save open accordion indexes to localStorage
const saveOpenAccordionIndexes = (indexes: number[]): void => {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(OPEN_ACCORDION_INDEXES_KEY, JSON.stringify(indexes));
  } catch (error) {
    console.error("Failed to save open accordion indexes to localStorage:", error);
  }
};

// Create a store for global debug information that won't trigger re-renders
interface DebugStoreState {
  renderTally: Map<string, number>;
  resetKey: number; // Reset key to force updates
  openAccordionIndexes: number[]; // Track which accordion items are open
  tallyRender: (componentName: string) => void;
  getRenderTally: () => Map<string, number>;
  getSortedRenderTally: () => Array<[string, number]>;
  resetRenderTally: () => void;
  setOpenAccordionIndexes: (indexes: number[]) => void;
  getOpenAccordionIndexes: () => number[];
}

export const useDebugStore = create<DebugStoreState>()((set, get) => ({
  renderTally: loadRenderTally(),
  resetKey: 0,
  openAccordionIndexes: loadOpenAccordionIndexes(),

  tallyRender: (componentName: string) => {
    const currentTally = get().renderTally;

    if (!currentTally.has(componentName)) {
      currentTally.set(componentName, 1);
    } else {
      const count = currentTally.get(componentName) || 0;
      currentTally.set(componentName, count + 1);
    }

    // Save to localStorage for persistence across refreshes
    saveRenderTally(currentTally);

    // This update won't trigger re-renders because we're modifying the same Map reference
    set({ renderTally: currentTally });
  },

  getRenderTally: () => {
    return get().renderTally;
  },

  getSortedRenderTally: () => {
    const tally = get().renderTally;
    return Array.from(tally.entries()).sort((a, b) => b[1] - a[1]);
  },

  resetRenderTally: () => {
    // Create a new Map instance to trigger re-renders
    const emptyTally = new Map<string, number>();

    // Clear localStorage
    localStorage.removeItem(RENDER_TALLY_KEY);

    // Increment the reset key to force updates
    const newResetKey = get().resetKey + 1;

    // Update the state with a new Map reference to trigger re-renders
    set({ renderTally: emptyTally, resetKey: newResetKey });

    console.log("Debug tally reset to empty state, reset key:", newResetKey);
  },

  setOpenAccordionIndexes: (indexes: number[]) => {
    saveOpenAccordionIndexes(indexes);
    set({ openAccordionIndexes: indexes });
  },

  getOpenAccordionIndexes: () => {
    return get().openAccordionIndexes;
  }
}));
